/**
 * Ledger Service
 * General Ledger and Journal Entry management
 * Double-entry bookkeeping operations
 */

import { supabase } from '@/lib/supabase'
import type { JournalEntry, JournalEntryLine } from './financeService'

// =====================================================
// GENERAL LEDGER SERVICE
// =====================================================

export class LedgerService {
  /**
   * Get journal entries with optional filters
   */
  static async getJournalEntries(
    churchId: string,
    filters?: {
      status?: string
      sourceType?: string
      fiscalYear?: number
      fiscalPeriod?: number
      startDate?: string
      endDate?: string
      search?: string
    }
  ): Promise<JournalEntry[]> {
    let query = supabase
      .from('general_ledger')
      .select(`
        *,
        lines:general_ledger_lines(
          *,
          account:chart_of_accounts(*)
        )
      `)
      .eq('church_id', churchId)
      .order('entry_date', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.sourceType) {
      query = query.eq('source_type', filters.sourceType)
    }

    if (filters?.fiscalYear) {
      query = query.eq('fiscal_year', filters.fiscalYear)
    }

    if (filters?.fiscalPeriod) {
      query = query.eq('fiscal_period', filters.fiscalPeriod)
    }

    if (filters?.startDate) {
      query = query.gte('entry_date', filters.startDate)
    }

    if (filters?.endDate) {
      query = query.lte('entry_date', filters.endDate)
    }

    if (filters?.search) {
      query = query.or(`entry_number.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get journal entry by ID
   */
  static async getJournalEntryById(entryId: string): Promise<JournalEntry | null> {
    const { data, error } = await supabase
      .from('general_ledger')
      .select(`
        *,
        lines:general_ledger_lines(
          *,
          account:chart_of_accounts(*)
        )
      `)
      .eq('id', entryId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create journal entry (draft)
   */
  static async createJournalEntry(
    entry: Partial<JournalEntry>,
    lines: Partial<JournalEntryLine>[]
  ): Promise<JournalEntry> {
    // Validate double-entry (debits = credits)
    const totalDebits = lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0)
    const totalCredits = lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0)

    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      throw new Error(`Journal entry is not balanced. Debits: ${totalDebits}, Credits: ${totalCredits}`)
    }

    // Generate entry number if not provided
    if (!entry.entry_number) {
      const date = new Date(entry.entry_date!)
      const prefix = entry.source_type === 'manual' ? 'JE' : entry.source_type?.toUpperCase().substring(0, 3)
      const datePart = date.toISOString().slice(0, 7).replace('-', '')
      entry.entry_number = `${prefix}-${datePart}-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
    }

    // Set fiscal year and period from entry date
    const entryDate = new Date(entry.entry_date!)
    entry.fiscal_year = entryDate.getFullYear()
    entry.fiscal_period = entryDate.getMonth() + 1

    // Create header
    const { data: headerData, error: headerError } = await supabase
      .from('general_ledger')
      .insert([{
        ...entry,
        total_debit: totalDebits,
        total_credit: totalCredits,
        status: entry.status || 'draft'
      }])
      .select()
      .single()

    if (headerError) throw headerError

    // Create lines
    const linesToInsert = lines.map((line, index) => ({
      ...line,
      journal_entry_id: headerData.id,
      church_id: entry.church_id,
      line_number: line.line_number || index + 1
    }))

    const { error: linesError } = await supabase
      .from('general_ledger_lines')
      .insert(linesToInsert)

    if (linesError) throw linesError

    // Return the complete entry
    return this.getJournalEntryById(headerData.id) as Promise<JournalEntry>
  }

  /**
   * Update journal entry (only if draft)
   */
  static async updateJournalEntry(
    entryId: string,
    updates: Partial<JournalEntry>,
    lines?: Partial<JournalEntryLine>[]
  ): Promise<JournalEntry> {
    // Check if entry is still draft
    const existing = await this.getJournalEntryById(entryId)
    if (!existing || existing.status !== 'draft') {
      throw new Error('Only draft journal entries can be modified')
    }

    // If lines are provided, validate and update them
    if (lines) {
      const totalDebits = lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0)
      const totalCredits = lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0)

      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error(`Journal entry is not balanced. Debits: ${totalDebits}, Credits: ${totalCredits}`)
      }

      // Delete existing lines
      await supabase
        .from('general_ledger_lines')
        .delete()
        .eq('journal_entry_id', entryId)

      // Insert new lines
      const linesToInsert = lines.map((line, index) => ({
        ...line,
        journal_entry_id: entryId,
        church_id: existing.church_id,
        line_number: line.line_number || index + 1
      }))

      await supabase
        .from('general_ledger_lines')
        .insert(linesToInsert)

      updates.total_debit = totalDebits
      updates.total_credit = totalCredits
    }

    // Update header
    const { error } = await supabase
      .from('general_ledger')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', entryId)

    if (error) throw error

    return this.getJournalEntryById(entryId) as Promise<JournalEntry>
  }

  /**
   * Post journal entry (change status from draft to posted)
   */
  static async postJournalEntry(entryId: string, postedBy: string): Promise<void> {
    const { data, error } = await supabase.rpc('post_journal_entry', {
      p_journal_entry_id: entryId,
      p_posted_by: postedBy
    })

    if (error) throw error
  }

  /**
   * Void journal entry (reverse it)
   */
  static async voidJournalEntry(
    entryId: string,
    voidReason: string,
    voidedBy: string
  ): Promise<string> {
    const { data, error } = await supabase.rpc('void_journal_entry', {
      p_journal_entry_id: entryId,
      p_void_reason: voidReason,
      p_voided_by: voidedBy
    })

    if (error) throw error
    return data // Returns the reversal entry ID
  }

  /**
   * Delete journal entry (only if draft and no references)
   */
  static async deleteJournalEntry(entryId: string): Promise<void> {
    // Check if entry is still draft
    const existing = await this.getJournalEntryById(entryId)
    if (!existing || existing.status !== 'draft') {
      throw new Error('Only draft journal entries can be deleted')
    }

    // Delete lines first (cascade should handle this, but being explicit)
    await supabase
      .from('general_ledger_lines')
      .delete()
      .eq('journal_entry_id', entryId)

    // Delete header
    const { error } = await supabase
      .from('general_ledger')
      .delete()
      .eq('id', entryId)

    if (error) throw error
  }

  /**
   * Get trial balance
   */
  static async getTrialBalance(
    churchId: string,
    asOfDate?: string
  ): Promise<any[]> {
    let query = supabase
      .from('v_trial_balance')
      .select('*')
      .eq('church_id', churchId)

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get account ledger (transactions for a specific account)
   */
  static async getAccountLedger(
    accountId: string,
    startDate?: string,
    endDate?: string
  ): Promise<JournalEntryLine[]> {
    let query = supabase
      .from('general_ledger_lines')
      .select(`
        *,
        journal_entry:general_ledger(*),
        account:chart_of_accounts(*)
      `)
      .eq('account_id', accountId)

    if (startDate) {
      query = query.gte('journal_entry.entry_date', startDate)
    }

    if (endDate) {
      query = query.lte('journal_entry.entry_date', endDate)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }
}

export default LedgerService
